import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Medal, Award, Gift } from "lucide-react";

const PODIUM = [
  { pos: "1°", prize: 8000, icon: Trophy, color: "from-[#FFD700] to-[#B8860B]", label: "Primo classificato" },
  { pos: "2°", prize: 4000, icon: Medal, color: "from-[#E0E0E0] to-[#8A8A8A]", label: "Secondo classificato" },
  { pos: "3°", prize: 2000, icon: Award, color: "from-[#CD7F32] to-[#8B4513]", label: "Terzo classificato" },
];

export default function Prizes() {
  return (
    <section id="premi" className="relative py-24 md:py-32 px-5 md:px-8" data-testid="prizes-section">
      <div className="absolute inset-0 radial-glow opacity-60" />
      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs tracking-[0.4em] uppercase text-fp-cyan font-heading font-semibold">
            Montepremi
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tight silver-gradient">
            Oltre <span className="cyan-gradient">30.000€</span> di montepremi
          </h2>
          <p className="mt-5 text-white/60 max-w-2xl mx-auto">
            Premi in denaro fino al 60° classificato. Ogni euro iscritto torna in palio.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6 mb-12">
          {PODIUM.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.pos}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className={`relative p-8 rounded-3xl border border-white/10 bg-fp-surface/40 backdrop-blur-sm overflow-hidden group hover:-translate-y-2 hover:border-fp-cyan/50 transition-all duration-500 ${
                  i === 0 ? "md:scale-105 md:-mt-4" : ""
                }`}
                data-testid={`prize-card-${i + 1}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${p.color} opacity-0 group-hover:opacity-10 transition-opacity`} />
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${p.color} flex items-center justify-center shadow-lg`}>
                  <Icon size={28} className="text-white" />
                </div>
                <div className="mt-6 font-heading font-black text-7xl silver-gradient">{p.pos}</div>
                <div className="mt-3 text-white/60 text-sm">{p.label}</div>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">
                    Premio
                  </div>
                  <div className="mt-2 font-heading font-black text-4xl text-white">
                    {p.prize.toLocaleString("it-IT")}€
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative p-8 md:p-10 rounded-3xl border border-fp-cyan/30 bg-gradient-to-br from-fp-cyan/5 to-transparent flex flex-col md:flex-row items-start md:items-center gap-6 overflow-hidden"
          data-testid="prize-extra"
        >
          <div className="w-14 h-14 rounded-2xl bg-fp-cyan/20 flex items-center justify-center flex-shrink-0">
            <Gift size={28} className="text-fp-cyan" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-xl md:text-2xl">
              Premi fino al 60° classificato
            </h3>
            <p className="mt-2 text-white/60 text-sm md:text-base">
              Oltre al podio, riconoscimenti in denaro per decine di posizioni e premi tematici
              delle competizioni collaterali (Autumn, Winter, Spring, Champions, Highlander, Return).
            </p>
          </div>
          <Link
            to="/premi/gallery"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-fp-cyan text-fp-cyan font-heading font-semibold text-sm tracking-wider uppercase hover:bg-fp-cyan hover:text-fp-bg transition-all"
            data-testid="view-all-prizes-btn"
          >
            Scopri tutti gli altri premi →
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
